import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Mail, MessageCircle } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { useCommunicationsWorkspace } from '../../context/CommunicationsWorkspaceContext';
import API_BASE from '../../utils/apiBase';
import { Button } from '../ui/button';
import { recruitmentMessageState, recruitmentWhatsAppFailure } from '../../pages/portal/recruitmentInboxHelpers';

const API = API_BASE;

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export default function ApplicantRecruitmentCommunicationSummary() {
  const { employeeId } = useParams();
  const location = useLocation();
  const { token } = useAuth();
  const { openApplicant } = useCommunicationsWorkspace();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshVersion, setRefreshVersion] = useState(0);

  const isApplicantRoute = location.pathname.startsWith('/portal/recruitment/');

  useEffect(() => {
    const handleCommunicationUpdated = (event) => {
      if (String(event?.detail?.employeeId || '') !== String(employeeId || '')) return;
      setRefreshVersion((value) => value + 1);
    };
    window.addEventListener('careops:recruitment-communication-updated', handleCommunicationUpdated);
    return () => {
      window.removeEventListener('careops:recruitment-communication-updated', handleCommunicationUpdated);
    };
  }, [employeeId]);

  useEffect(() => {
    let active = true;
    if (!isApplicantRoute || !employeeId || !token) {
      setLoading(false);
      return () => { active = false; };
    }

    setLoading(true);
    fetch(`${API}/employees/${employeeId}/communications`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Could not load recruitment communication summary');
        return response.json();
      })
      .then((data) => {
        if (active) setPayload(data || null);
      })
      .catch(() => {
        if (active) setPayload(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [employeeId, isApplicantRoute, token, refreshVersion]);

  const summary = useMemo(() => {
    const messages = Array.isArray(payload?.messages) ? payload.messages : [];
    const latest = messages.length ? messages[messages.length - 1] : null;
    return {
      thread: payload?.thread || null,
      latest,
      state: latest ? recruitmentMessageState(latest) : null,
      failure: latest ? recruitmentWhatsAppFailure(latest) : null,
    };
  }, [payload]);

  if (!isApplicantRoute) return null;

  const href = summary.thread?.id
    ? `/portal/inbox?department=recruitment&thread=${encodeURIComponent(summary.thread.id)}`
    : '/portal/inbox?department=recruitment';
  const channel = String(summary.latest?.channel || 'email').toLowerCase();
  const ChannelIcon = channel === 'whatsapp' ? MessageCircle : Mail;

  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="applicant-recruitment-communication-summary">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-semibold text-slate-900">Applicant communication</p>
          {loading ? (
            <p className="mt-1 text-xs text-slate-500">Loading Recruitment Inbox summary…</p>
          ) : summary.latest ? (
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-600">
              <span className="inline-flex items-center gap-1">
                <ChannelIcon className="h-3.5 w-3.5" />
                {channel === 'whatsapp' ? 'WhatsApp' : 'Email'}
              </span>
              <span>·</span>
              <span>{summary.state?.label}</span>
              <span>·</span>
              <span>{formatDateTime(summary.latest.created_at)}</span>
              {Number(summary.thread?.unread_count || 0) > 0 ? (
                <span className="font-semibold text-red-700">· {summary.thread.unread_count} unread</span>
              ) : null}
            </div>
          ) : (
            <p className="mt-1 text-xs text-slate-500">No Recruitment Inbox conversation has been recorded for this applicant yet.</p>
          )}
          {summary.failure ? (
            <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-900">
              <p className="font-semibold">The latest WhatsApp message was not delivered.</p>
              {summary.failure.recipient ? (
                <p>Attempted destination: <strong>+{summary.failure.recipient}</strong></p>
              ) : null}
              <p>{summary.failure.code ? `Meta error ${summary.failure.code}: ` : ''}{summary.failure.reason}</p>
            </div>
          ) : null}
          <p className="mt-2 text-xs text-slate-500">
            Full email and WhatsApp history is kept in the Recruitment Inbox, separate from staff communications.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => openApplicant({ employeeId, threadId: summary.thread?.id || null })}
            data-testid="open-communications-workspace"
          >
            <MessageCircle className="mr-1.5 h-4 w-4" />
            Message
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to={href}>Full Inbox</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
